import React, { useState } from 'react';
import {
  Popover,
  Grid,
  Button,
  useTheme,
  alpha,
  Box, // Box is still used for the checkmark
  Typography,
  Stack
} from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';

// Color definitions
const COLORS = ['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FFA500', '#800080', '#FFC0CB', '#A52A2A', '#808080'];

function ColorPickerButton({ color, setColor, label }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (c) => {
    setColor(c);
    handleClose(); // Close the popover after selecting a color
  };

  const open = Boolean(anchorEl);
  const id = open ? `color-picker-popover-${label.replace(/\s/g, '')}` : undefined;

  // Provide a default color if `color` is null or undefined
  const displayColor = color || '#000000'; // Use transparent for display if no color is selected
  const textColor = color ? theme.palette.getContrastText(color) : '#000000'; // Default to black text if no color

  return (
    <>
      <Button
        variant="contained"
        onClick={handleClick}
        startIcon={<ColorLensIcon />}
        sx={{
          color: textColor,
          backgroundColor: displayColor,
          '&:hover': {
            backgroundColor: alpha(displayColor, 0.9),
          },
        }}
        title={`${label} Color Picker`}
        aria-describedby={id}
      >
        {label}
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
            sx: {
                p: 1,
                mt: 1,
            }
        }}
      >
        <Grid container spacing={1} sx={{ maxWidth: 180 }}>
          {COLORS.map((c) => (
            <Grid item xs={4} key={c}>
              <Button
                onClick={() => handleColorSelect(c)}
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 0,
                  p: 0,
                  borderRadius: '50%',
                  backgroundColor: c,
                  border: c === color ? `3px solid ${theme.palette.primary.main}` : 'none',
                  '&:hover': {
                    backgroundColor: c,
                    opacity: 0.8,
                  },
                }}
                title={c}
                aria-label={`Select color ${c}`}
              >
                {c === color && (
                    <Box sx={{ color: theme.palette.getContrastText(c), fontSize: '1.2rem' }}>
                        ✓
                    </Box>
                )}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Popover>
    </>
  );
}

export function ColorPickerToolbar({ color, setColor, fillColor, setFillColor }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="subtitle2">Stroke:</Typography>
      <ColorPickerButton color={color} setColor={setColor} label="Stroke" />
      <Typography variant="subtitle2">Fill:</Typography>
      <ColorPickerButton color={fillColor} setColor={setFillColor} label="Fill" />
    </Stack>
  );
}

export default ColorPickerButton;